import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Icon, Button, Loader, Dropdown } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import { PILink } from '../../nav';
import i18next from 'i18next';
import CartItem from './CartItem';
import CurrencyFormat, { attachCurrency } from '../../general/CurrencyFormat';
import { getCartItems } from '../../../selectors';
import { setOrderTip } from '../../../actions';

import './Cart.scss';

const Cart = ({ closeCart, readOnly }) => {
    const dispatch = useDispatch();
    const cartItemsToAnimate = useSelector(state => state.ui.cartItemsToAnimate);
    const requestedTipPercentage = useSelector(state => state.order.requestedTipPercentage);
    const tippingOptions = useSelector(state => state.business.branch.tippingOptions);
    const items = useSelector(state => getCartItems(state));
    const summary = useSelector(state => state.order.checkSummary);
    const isLoading = useSelector(state => state.loading.queue.includes('orders/validate'));
    const history = useHistory();

    const renderTipOptions = () => {
        const defaultValues = tippingOptions.defaultValues.map(value => value / 100);
        const customTipsOptions = new Array(31).fill().map((_, i) => ({ value: i / 100, text: `${i}%` }));
        const isCustomValue = requestedTipPercentage && !defaultValues.includes(requestedTipPercentage);

        const trigger = (
            <Button
                ket="custom"
                disabled={readOnly}                
                active={!!requestedTipPercentage && !defaultValues.includes(requestedTipPercentage)}
            >
                {isCustomValue ? `${(requestedTipPercentage * 100).toFixed()}%` : i18next.t('payment.customTip')}
            </Button>
        );

        return (
            <Button.Group basic fluid toggle widths={defaultValues.length + 1}>
                <Dropdown
                    trigger={trigger}
                    options={customTipsOptions}
                    scrolling
                    icon={null}
                    className={`custom-tip-picker`}
                    value={requestedTipPercentage}
                    onChange={(e, { value }) => {
                        dispatch(setOrderTip(value));
                    }}
                />
                {defaultValues.map(value => (
                    <Button
                        key={value}
                        disabled={readOnly}
                        active={requestedTipPercentage === value}
                        onClick={() => dispatch(setOrderTip(value))}
                    >
                        {`${value * 100}%`}
                    </Button>
                ))}
            </Button.Group>
        );
    };

    const renderConfirmButton = readOnly => {
        if (readOnly) {
            return <Button primary fluid onClick={() => history.goBack()}>{i18next.t('order.moreItems')}</Button>;
        }

        let content = `${i18next.t('payment.toPayment')} | ${attachCurrency(summary.paymentAmount)}`;
        if (!summary.orderMinimumFulfilled) content = i18next.t('order.orderMinimumNotFulfilled');
        if (!summary.isBusinessAvailable) content = i18next.t('order.closeForOrders');

        return (
            <Button
                primary
                fluid
                loading={isLoading}
                disabled={!items.length || !summary.isBusinessAvailable || !summary.orderMinimumFulfilled}
                as={PILink}
                to={'/checkout'}
                replace={false}
            >
                {content}
            </Button>
        );
    };

    return (
        <Card fluid className="pi-cart">
            <Card.Content className="cart-header">
                <span>{i18next.t('order.myCart')}</span>
                <Icon name="x" size="large" onClick={closeCart} />
            </Card.Content>
            <Card.Content className="cart-items">
                {items.slice().reverse().map(item => (
                    <CartItem
                        key={item.cartItem.index}
                        item={item}
                        readOnly={readOnly}
                        animate={cartItemsToAnimate.includes(item.cartItem)}
                    />
                ))}
                {!items.length &&
                    <Card.Content extra className="cart-empty">
                        <Icon name='food' />{i18next.t('order.cartEmpty')}
                    </Card.Content>
                }
            </Card.Content>
            {!tippingOptions ? null :
                <Card.Content className="cart-tip">
                    <div className="cart-tip-header">{i18next.t('payment.tip')}</div>
                    <Card.Description>
                        {renderTipOptions()}
                    </Card.Description>
                </Card.Content>
            }
            <Card.Content className="cart-summary">
                <div>
                    <span>{i18next.t('payment.subtotal')} ({items.length})</span>
                    {isLoading ? <Loader active inline size="mini" /> : <CurrencyFormat amount={summary.subtotal} />}
                </div>
                {summary.benefitsDiscount > 0 && (
                    <div style={{ opacity: Number(!isLoading) }}>
                        <span>{i18next.t('payment.discount')}</span>
                        <CurrencyFormat amount={summary.benefitsDiscount * -1} />
                    </div>
                )}
                {summary.deliveryFee > 0 && (
                    <div style={{ opacity: Number(!isLoading) }}>
                        <span>{i18next.t('payment.deliveryFees')}</span>
                        <CurrencyFormat amount={summary.deliveryFee} />
                    </div>
                )}
                {summary.calcedTip > 0 && (
                    <div style={{ opacity: Number(!isLoading) }}>
                        <span>{i18next.t('payment.tip')}</span>
                        <CurrencyFormat amount={summary.calcedTip} />
                    </div>
                )}
                {summary.calcedTaxes && summary.calcedTaxes.additionalAmount > 0 && (
                    <div style={{ opacity: Number(!isLoading) }}>
                        <span>{i18next.t(`payment.${summary.calcedTaxes.type || 'tax'}`)}</span>
                        <CurrencyFormat amount={summary.calcedTaxes.additionalAmount} />
                    </div>
                )}
                {summary.calcedTaxes && summary.calcedTaxes.surcharge > 0 && (
                    <div style={{ opacity: Number(!isLoading) }}>
                        <span>{i18next.t('payment.surcharge')}</span>
                        <CurrencyFormat amount={summary.calcedTaxes.surcharge} />
                    </div>
                )}
                {readOnly && (
                    <div className="total-payment">
                        <span>{i18next.t('payment.total')}</span>
                        {isLoading ? <Loader active inline size="mini" /> : <CurrencyFormat amount={summary.paymentAmount} />}
                    </div>
                )}
            </Card.Content>
            <Card.Content className="confirm-order">
                {renderConfirmButton(readOnly)}
            </Card.Content>
        </Card>
    );
};

Cart.propTypes = {
    closeCart: PropTypes.func,
    readOnly: PropTypes.bool
};

export default Cart;
